import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Archive, User, Briefcase, Calendar, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ArchivedEmployee {
  id: string;
  name: string;
  position: string;
  employee_number?: string;
  department?: string;
  archived_at: string;
}

export function ArchivedEmployeesList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<ArchivedEmployee[]>([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<ArchivedEmployee | null>(null);

  useEffect(() => {
    if (user) {
      loadArchivedEmployees();
    }
  }, [user]);

  const loadArchivedEmployees = async () => {
    try {
      const { data, error } = await supabase
        .from("employees")
        .select("id, name, position, employee_number, department, archived_at")
        .not("archived_at", "is", null)
        .order("archived_at", { ascending: false });

      if (error) throw error;
      setEmployees(data || []);
    } catch (error) {
      console.error("Error loading archived employees:", error);
      toast.error("Fehler beim Laden der archivierten Mitarbeiter");
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!selectedEmployee) return;
    
    setRestoring(selectedEmployee.id);
    try {
      const { error } = await supabase
        .from("employees")
        .update({ archived_at: null, archived_by: null })
        .eq("id", selectedEmployee.id);

      if (error) throw error;

      toast.success(`${selectedEmployee.name} wurde wiederhergestellt`);
      setEmployees(prev => prev.filter(e => e.id !== selectedEmployee.id));
    } catch (error) {
      console.error("Error restoring employee:", error);
      toast.error("Fehler beim Wiederherstellen");
    } finally {
      setRestoring(null);
      setShowRestoreDialog(false);
      setSelectedEmployee(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Archive className="h-5 w-5" />
            Archivierte Mitarbeiter
          </CardTitle>
          <CardDescription>
            Ehemalige Mitarbeiter, die archiviert wurden
          </CardDescription>
        </CardHeader>
        <CardContent>
          {employees.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Archive className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Keine archivierten Mitarbeiter vorhanden</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {employees.map((employee) => (
                <Card key={employee.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                        <User className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold truncate">{employee.name}</h3>
                        <div className="space-y-1 mt-1">
                          {employee.position && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Briefcase className="h-3 w-3" />
                              <span className="truncate">{employee.position}</span>
                            </div>
                          )}
                          {employee.archived_at && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Calendar className="h-3 w-3" />
                              <span>
                                Archiviert am {new Date(employee.archived_at).toLocaleDateString("de-DE")}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/employee/${employee.id}`)}
                          >
                            Ansehen
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              setSelectedEmployee(employee);
                              setShowRestoreDialog(true);
                            }}
                            disabled={restoring === employee.id}
                          >
                            {restoring === employee.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RotateCcw className="h-4 w-4 mr-1" />
                            )}
                            Wiederherstellen
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mitarbeiter wiederherstellen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie "{selectedEmployee?.name}" aus dem Archiv wiederherstellen?
              Der Mitarbeiter wird wieder in der aktiven Liste angezeigt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestore}>
              Wiederherstellen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
